import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, PrimaryButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { CLASSES } from "@/lib/status";

const EMPTY = { parent_name: "", parent_email: "", parent_phone: "", student_name: "", class_level: "", school_id: "none", consent_form_reference: "", mark_paid: false, payment_reference: "" };

function F({ label, children }) {
  return <div className="space-y-2"><Label className="font-semibold">{label}</Label>{children}</div>;
}

export default function CreateAccount() {
  const navigate = useNavigate();
  const { data: schools } = useFetch("/admin/schools");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.class_level) return toast.error("Please select the student's class");
    setBusy(true);
    try {
      const payload = { ...form, class_level: Number(form.class_level), school_id: form.school_id === "none" ? null : form.school_id };
      const { data } = await api.post("/admin/accounts", payload);
      toast.success("Account created. A set-password link has been emailed to the parent.");
      navigate(`/admin/parents/${data.parent_id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="create-account-page">
      <PageHeader eyebrow="Provisioning" title="Create a family account" description="Use this after the parent has signed the physical consent form. The parent receives a secure link to set their own password." />
      <form onSubmit={submit} className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <section className="card-surface p-6 space-y-5">
          <h2 className="font-display text-lg font-bold">Parent</h2>
          <F label="Parent's full name"><Input required minLength={2} value={form.parent_name} onChange={set("parent_name")} className="h-11" data-testid="create-parent-name-input" /></F>
          <F label="Parent's email"><Input type="email" required value={form.parent_email} onChange={set("parent_email")} className="h-11" data-testid="create-parent-email-input" /></F>
          <F label="Phone (optional)"><Input value={form.parent_phone} onChange={set("parent_phone")} className="h-11" data-testid="create-parent-phone-input" /></F>
          <F label="Signed consent form reference"><Input value={form.consent_form_reference} onChange={set("consent_form_reference")} placeholder="e.g. Form #SPR-0042" className="h-11" data-testid="create-consent-ref-input" /></F>
        </section>
        <section className="card-surface p-6 space-y-5">
          <h2 className="font-display text-lg font-bold">Student</h2>
          <F label="Student's name"><Input required minLength={2} value={form.student_name} onChange={set("student_name")} className="h-11" data-testid="create-student-name-input" /></F>
          <F label="Class">
            <Select value={form.class_level} onValueChange={(v) => setForm({ ...form, class_level: v })}>
              <SelectTrigger className="h-11" data-testid="create-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent className="bg-white">{CLASSES.map((c) => <SelectItem key={c} value={String(c)} data-testid={`create-class-option-${c}`}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Partner school (optional)">
            <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
              <SelectTrigger className="h-11" data-testid="create-school-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">No school (platform branding)</SelectItem>
                {schools?.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <div className="rounded-xl border border-[#E9DFE7] p-4 space-y-3">
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold">Payment already collected offline</span>
              <Switch checked={form.mark_paid} onCheckedChange={(v) => setForm({ ...form, mark_paid: v })} data-testid="create-mark-paid-switch" />
            </label>
            {form.mark_paid && <Input required value={form.payment_reference} onChange={set("payment_reference")} placeholder="Payment reference" className="h-11" data-testid="create-payment-ref-input" />}
          </div>
        </section>
        <div className="lg:col-span-2"><PrimaryButton type="submit" busy={busy} className="h-12 px-8" data-testid="create-account-submit">Create account and email parent</PrimaryButton></div>
      </form>
    </div>
  );
}
